import { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Spinner } from 'react-bootstrap'

import CareerFilters from './CareerFilters'
import VacancyCard from './VacancyCard'
import Paginator from '../Paginator/Paginator'
import Disclaimer from '../Disclaimer/Disclaimer'
import DataNotfound from '../DataNotfound/DataNotfound'
import ErrorComponent from '../ErrorComponent/ErrorComponent'
import useIsMobile from '../../hooks/useIsMobile'
import useFetchFilteredData from '../../hooks/useFetchFilteredData'
import { getTotalPages } from '../../utils'
import { CAREER } from '../../Contansts'
import styles from './CareerLayout.module.scss'

function CareerLayout({ careerData, pagination: initialPagination }) {
  const {
    careertitle: careerTitle,
    careersubtitle: careerSubtitle,
    location_filter,
    function_filter,
    vacanciesData,
    disclaimer_title,
    disclaimer_description,
    emptyMessage = '',
  } = careerData || {}

  const [selectedFilter, setSelectedFilter] = useState({
    selectedlocation: location_filter?.list ? location_filter.list[0] : null,
    selectedFunction: function_filter?.list ? function_filter.list[0] : null,
  })
  const [totalPages, setTotalPages] = useState(getTotalPages(initialPagination))
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [vacancies, setVacancies] = useState(vacanciesData)
  const isMobile = useIsMobile()
  const loaderRef = useRef(null)

  const {
    data: filteredData,
    loading,
    error,
    setParams,
  } = useFetchFilteredData(CAREER)

  const handleFilterChange = (e, option, key) => {
    setSelectedFilter((prevState) => ({
      ...prevState,
      [key]: {
        ...option,
      },
    }))
  }

  useEffect(() => {
    if (isInitialLoading) {
      return
    }
    const { result = {}, pagination } = filteredData?.data || {}
    if (pagination) {
      setTotalPages(getTotalPages(pagination))
    }
    const vacanciesData = result?.sectiondata?.vacanciesData || []
    if (isMobile) {
      setVacancies((prevData) => [...prevData, ...vacanciesData])
    } else {
      setVacancies(vacanciesData)
    }
  }, [filteredData])

  useEffect(() => {
    if (isInitialLoading) {
      setIsInitialLoading(false)
      return
    }
    const params = `location_filter=${selectedFilter?.selectedlocation?.value || ''}&function_filter=${selectedFilter?.selectedFunction?.value || ''}&page=${currentPage}`
    setParams(params)
  }, [selectedFilter, currentPage])

  const lazyLoad = useCallback(
    (entries) => {
      const target = entries[0]
      if (target.isIntersecting && !loading) {
        if (currentPage < totalPages) {
          setCurrentPage((prevPage) => prevPage + 1)
        }
      }
    },
    [loading, totalPages, filteredData]
  )

  useEffect(() => {
    if (isMobile) {
      const observer = new IntersectionObserver(lazyLoad)
      if (loaderRef.current) {
        observer.observe(loaderRef.current)
      }
      return () => {
        if (loaderRef.current) {
          observer.unobserve(loaderRef.current)
        }
      }
    }
  }, [lazyLoad])

  const renderFilteredData = () => {
    if (loading && !isMobile) {
      return (
        <div className="flex justify-center md:p-52">
          <Spinner />
        </div>
      )
    }
    if (error?.hasError) {
      return (
        <div className="flex justify-center">
          <ErrorComponent
            errorMessage={error.errorMessage}
            renderProps={() => <div>{error.errorMessage}</div>}
          />
        </div>
      )
    }
    if (vacancies?.length > 0) {
      return (
        <>
          <div className={styles.cardsListContainer}>
            {vacancies.map((vacancy) => (
              <VacancyCard key={vacancy?.id} vacancy={vacancy} />
            ))}
          </div>
          {loading && isMobile && (
            <div className="flex justify-center">
              <Spinner />
            </div>
          )}
          <div ref={loaderRef}></div>
          {!isMobile && (
            <Paginator
              currentPage={currentPage}
              numOfPage={totalPages}
              setCurrentPage={setCurrentPage}
              className={'m-0 justify-center'}
            />
          )}
        </>
      )
    }
    if (vacancies?.length === 0) {
      return <DataNotfound description={emptyMessage} />
    }
  }

  return (
    <div className={classNames('page-container', styles.careerContainer)}>
      <div className={styles.topContainer}>
        <div className={styles.careerHeaderContainer}>
          <span className={styles.careerTitle}>{careerTitle}</span>
          <span className={styles.careerSubtitle}>{careerSubtitle}</span>
        </div>
        <div className={styles.filtersContainer}>
          <CareerFilters
            selectedFilter={selectedFilter}
            locationFilter={location_filter}
            functionFilter={function_filter}
            handleFilterChange={handleFilterChange}
          />
        </div>
        {renderFilteredData()}
      </div>
      <div className={styles.bottomContainer}>
        <Disclaimer
          title={disclaimer_title}
          description={disclaimer_description}
        />
      </div>
    </div>
  )
}

export default CareerLayout
